// frontend/packages/common/utils/axios.js
import axios from 'axios';
import {HOST_API} from 'apps/dashboard/ConfigGlobal';

/**
 * Création de l'instance Axios avec la configuration de base
 */
const axiosInstance = axios.create({
  baseURL: HOST_API,
  withCredentials: true, // Nécessaire pour envoyer/recevoir les cookies
});

/**
 * Configure les options pour une requête avec API key (API Externe)
 * @param {string} apiKey - Clé API à utiliser
 * @return {Object} - Configuration avec API key
 */
export const withApiKey = (apiKey) => {
  if (!apiKey) {
    console.warn('Attention: clé API manquante');
    return {};
  }

  // Verify we're using a valid API key format (should start with pub_)
  if (apiKey && !apiKey.startsWith('pub_')) {
    console.error('Format de clé API invalide:', apiKey);
    return {};
  }

  return {
    headers: {
      'x-api-key': apiKey,
    },
  };
};

/**
 * Configuration des endpoints de l'API
 */
export const endpoints = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    register: '/auth/register',
    verifyEmail: (token) => `/auth/verify-email/${token}`,
    user: '/auth/profile',
    refreshToken: '/auth/refresh-token',
    verifyToken: '/auth/verify-token',
    requestResetPassword: '/auth/request-reset-password',
    resetPassword: '/auth/reset-password',
    verifyResetToken: (token) => `/auth/verify-reset-token?token=${token}`,
  },
  app: {
    user: {
      create: (companyId) => `/app/${companyId}/user/create-user`,
      update: (companyId, userId) => `/app/${companyId}/user/update-user/${userId}`,
      updateAsAdmin: (companyId, userId) => `/app/${companyId}/user/update-user-admin/${userId}`,
      getUsersByAccount: (companyId, accountId) => `/app/${companyId}/user/${accountId}/all`,
      toggleGiftVoucherPurchaseNotifications: (companyId, userId) =>
        `/app/${companyId}/user/notifications/gift-vouchers/purchase/${userId}`,
      changePassword: (companyId) => `/app/${companyId}/user/change-password`,
      toggleActive: (companyId, userId) => `/app/${companyId}/user/toggle-active/${userId}`,
    },
    company: {
      update: (companyId) => `/app/${companyId}/company/update`,
      stripeConnect: (companyId) => `/app/${companyId}/stripe/connect/standard`,
    },
    customer: {
      all: (companyId) => `/app/${companyId}/customer/all`,
      details: (companyId, customerId) => `/app/${companyId}/customer/details/${customerId}`,
      create: (companyId) => `/app/${companyId}/customer/create`,
      update: (companyId, customerId) => `/app/${companyId}/customer/update/${customerId}`,
      search: (companyId) => `/app/${companyId}/customer/search`,
    },
    employees: {
      employee: {
        all: (companyId) => `/app/${companyId}/employee/all`,
        details: (companyId, employeeId) => `/app/${companyId}/employee/details/${employeeId}`,
        create: (companyId) => `/app/${companyId}/employee/create`,
        update: (companyId, employeeId) => `/app/${companyId}/employee/update/${employeeId}`,
        delete: (companyId, employeeId) => `/app/${companyId}/employee/delete/${employeeId}`,
      },
      skill: {
        all: (companyId) => `/app/${companyId}/skill/all`,
        details: (companyId, skillId) => `/app/${companyId}/skill/details/${skillId}`,
        create: (companyId) => `/app/${companyId}/skill/create`,
        update: (companyId, skillId) => `/app/${companyId}/skill/update/${skillId}`,
        delete: (companyId, skillId) => `/app/${companyId}/skill/delete/${skillId}`,
      },
    },
    wellness: {
      config: {
        get: (companyId) => `/app/${companyId}/wellness/wellness-config/get`,
        create: (companyId) => `/app/${companyId}/wellness/wellness-config/create`,
        update: (companyId) => `/app/${companyId}/wellness/wellness-config/update`,
      },
      spaceGroup: {
        all: (companyId) => `/app/${companyId}/wellness/space-groups/all`,
        details: (companyId, spaceGroupId) => `/app/${companyId}/wellness/space-groups/details/${spaceGroupId}`,
        create: (companyId) => `/app/${companyId}/wellness/space-groups/create`,
        update: (companyId, spaceGroupId) => `/app/${companyId}/wellness/space-groups/update/${spaceGroupId}`,
        delete: (companyId, spaceGroupId) => `/app/${companyId}/wellness/space-groups/delete/${spaceGroupId}`,
        reorder: (companyId) => `/app/${companyId}/wellness/space-groups/reorder`,
      },
      space: {
        all: (companyId) => `/app/${companyId}/wellness/space/all`,
        details: (companyId, spaceId) => `/app/${companyId}/wellness/space/details/${spaceId}`,
        create: (companyId) => `/app/${companyId}/wellness/space/create`,
        update: (companyId, spaceId) => `/app/${companyId}/wellness/space/update/${spaceId}`,
        delete: (companyId, spaceId) => `/app/${companyId}/wellness/space/delete/${spaceId}`,
        reorder: (companyId) => `/app/${companyId}/wellness/space/reorder`,
      },
      serviceCategory: {
        all: (companyId) => `/app/${companyId}/wellness/service-category/all`,
        details: (companyId, categoryId) => `/app/${companyId}/wellness/service-category/details/${categoryId}`,
        create: (companyId) => `/app/${companyId}/wellness/service-category/create`,
        update: (companyId, categoryId) => `/app/${companyId}/wellness/service-category/update/${categoryId}`,
        delete: (companyId, categoryId) => `/app/${companyId}/wellness/service-category/delete/${categoryId}`,
      },
      service: {
        all: (companyId) => `/app/${companyId}/wellness/service/all`,
        details: (companyId, serviceId) => `/app/${companyId}/wellness/service/details/${serviceId}`,
        create: (companyId) => `/app/${companyId}/wellness/service/create`,
        update: (companyId, serviceId) => `/app/${companyId}/wellness/service/update/${serviceId}`,
        delete: (companyId, serviceId) => `/app/${companyId}/wellness/service/delete/${serviceId}`,
      },

      extraCategory: {
        all: (companyId) => `/app/${companyId}/wellness/extra-category/all`,
        details: (companyId, categoryId) => `/app/${companyId}/wellness/extra-category/details/${categoryId}`,
        create: (companyId) => `/app/${companyId}/wellness/extra-category/create`,
        update: (companyId, categoryId) => `/app/${companyId}/wellness/extra-category/update/${categoryId}`,
        delete: (companyId, categoryId) => `/app/${companyId}/wellness/extra-category/delete/${categoryId}`,
      },
      extra: {
        all: (companyId) => `/app/${companyId}/wellness/extra/all`,
        details: (companyId, extraId) => `/app/${companyId}/wellness/extra/details/${extraId}`,
        create: (companyId) => `/app/${companyId}/wellness/extra/create`,
        update: (companyId, extraId) => `/app/${companyId}/wellness/extra/update/${extraId}`,
        delete: (companyId, extraId) => `/app/${companyId}/wellness/extra/delete/${extraId}`,
      },
      productCategory: {
        all: (companyId) => `/app/${companyId}/wellness/product-category/all`,
        details: (companyId, categoryId) => `/app/${companyId}/wellness/product-category/details/${categoryId}`,
        create: (companyId) => `/app/${companyId}/wellness/product-category/create`,
        update: (companyId, categoryId) => `/app/${companyId}/wellness/product-category/update/${categoryId}`,
        delete: (companyId, categoryId) => `/app/${companyId}/wellness/product-category/delete/${categoryId}`,
      },
      product: {
        all: (companyId) => `/app/${companyId}/wellness/product/all`,
        details: (companyId, productId) => `/app/${companyId}/wellness/product/details/${productId}`,
        create: (companyId) => `/app/${companyId}/wellness/product/create`,
        update: (companyId, productId) => `/app/${companyId}/wellness/product/update/${productId}`,
        delete: (companyId, productId) => `/app/${companyId}/wellness/product/delete/${productId}`,
      },
      booking: {
        create: (companyId) => `/app/${companyId}/wellness/booking/create`,
        update: (companyId, bookingId) => `/app/${companyId}/wellness/booking/update/${bookingId}`,
        updateParticipants: (companyId, bookingId) => `/app/${companyId}/wellness/booking/participants/${bookingId}`,
        addSlot: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/add-slot`,
        removeSlot: (companyId, bookingId, slotId) => `/app/${companyId}/wellness/booking/${bookingId}/slots/${slotId}`,
        status: (companyId, bookingId) => `/app/${companyId}/wellness/booking/status/${bookingId}`,
        details: (companyId, bookingId) => `/app/${companyId}/wellness/booking/details/${bookingId}`,
        searchAll: (companyId) => `/app/${companyId}/wellness/booking/search-all`,
        planning: (companyId, bookingId) => `/app/${companyId}/wellness/booking/planning/${bookingId}`,
        cancelMultiple: (companyId) => `/app/${companyId}/wellness/booking/cancel-multiple`,
        addItems: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/add-items`,
      },
      availability: {
        sharedSpaceOccupancy: (companyId) => `/app/${companyId}/wellness/availability/shared-space-occupancy`,
      },
      transaction: {
        updateComplete: (companyId, transactionId) => `/app/${companyId}/wellness/transaction/${transactionId}/update-complete`,
        generateInvoice: (companyId, transactionId) => `/app/${companyId}/wellness/transaction/${transactionId}/invoice`,
      },
      payment: {
        add: (companyId, bookingId) => `/app/${companyId}/wellness/payment/${bookingId}/add`,
        cancel: (companyId, paymentId) => `/app/${companyId}/wellness/payment/${paymentId}/cancel`,
        history: (companyId, bookingId) => `/app/${companyId}/wellness/payment/${bookingId}/history`,
      },
      dailyClosings: {
        getAll: (companyId) => `/app/${companyId}/wellness/daily-closings`,
        getByDate: (companyId, date) => `/app/${companyId}/wellness/daily-closings/date/${date}`,
        export: (companyId, closingId) => `/app/${companyId}/wellness/daily-closings/${closingId}/export`,
        posStats: (companyId) => `/app/${companyId}/wellness/daily-closings/pos-stats`,
        create: (companyId) => `/app/${companyId}/wellness/daily-closings/create`,
        close: (companyId) => `/app/${companyId}/wellness/daily-closings/close`,
        reopen: (companyId) => `/app/${companyId}/wellness/daily-closings/reopen`,
      },
    },
    restaurant: {
      area: {
        all: (companyId) => `/app/${companyId}/restaurant/area/all`,
        details: (companyId, areaId) => `/app/${companyId}/restaurant/area/details/${areaId}`,
        create: (companyId) => `/app/${companyId}/restaurant/area/create`,
        update: (companyId, areaId) => `/app/${companyId}/restaurant/area/update/${areaId}`,
        delete: (companyId, areaId) => `/app/${companyId}/restaurant/area/delete/${areaId}`,
      },
      table: {
        all: (companyId) => `/app/${companyId}/restaurant/table/all`,
        details: (companyId, tableId) => `/app/${companyId}/restaurant/table/details/${tableId}`,
        create: (companyId) => `/app/${companyId}/restaurant/table/create`,
        update: (companyId, tableId) => `/app/${companyId}/restaurant/table/update/${tableId}`,
        delete: (companyId, tableId) => `/app/${companyId}/restaurant/table/delete/${tableId}`,
        duplicate: (companyId, tableId) => `/app/${companyId}/restaurant/table/duplicate/${tableId}`,
      },
      tableCombination: {
        all: (companyId) => `/app/${companyId}/restaurant/table-combination/all`,
        details: (companyId, combinationId) => `/app/${companyId}/restaurant/table-combination/details/${combinationId}`,
        create: (companyId) => `/app/${companyId}/restaurant/table-combination/create`,
        update: (companyId, combinationId) => `/app/${companyId}/restaurant/table-combination/update/${combinationId}`,
        delete: (companyId, combinationId) => `/app/${companyId}/restaurant/table-combination/delete/${combinationId}`,
      },
      floorPlanElement: {
        all: (companyId) => `/app/${companyId}/restaurant/floor-plan-element/all`,
        details: (companyId, elementId) => `/app/${companyId}/restaurant/floor-plan-element/details/${elementId}`,
        create: (companyId) => `/app/${companyId}/restaurant/floor-plan-element/create`,
        update: (companyId, elementId) => `/app/${companyId}/restaurant/floor-plan-element/update/${elementId}`,
        delete: (companyId, elementId) => `/app/${companyId}/restaurant/floor-plan-element/delete/${elementId}`,
        deleteAll: (companyId) => `/app/${companyId}/restaurant/floor-plan-element/delete-all`,
      },
      availability: {
        check: (companyId) => `/app/${companyId}/restaurant/availability/check`,
        dates: (companyId) => `/app/${companyId}/restaurant/availability/dates`,
        timeslots: (companyId) => `/app/${companyId}/restaurant/availability/timeslots`,
        invalidateCache: (companyId) => `/app/${companyId}/restaurant/availability/invalidate-cache`,
      },
      reservationFull: {
        getStatus: (companyId) => `/app/${companyId}/restaurant/reservation-full/status`,
        markServiceAsFull: (companyId) => `/app/${companyId}/restaurant/reservation-full/service`,
        markTimeslotAsFull: (companyId) => `/app/${companyId}/restaurant/reservation-full/timeslot`,
        removeServiceFullStatus: (companyId) => `/app/${companyId}/restaurant/reservation-full/service`,
        removeTimeslotFullStatus: (companyId) => `/app/${companyId}/restaurant/reservation-full/timeslot`,
        getAllPossibleTimeslots: (companyId) => `/app/${companyId}/restaurant/reservation-full/dashboard/timeslots`,
        getAllServicesForDate: (companyId) => `/app/${companyId}/restaurant/reservation-full/dashboard/services`,
        getClosedServices: (companyId) => `/app/${companyId}/restaurant/reservation-full/closed-services`,
      },
      reservation: {
        create: (companyId) => `/app/${companyId}/restaurant/reservation/create`,
        update: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/update/${reservationId}`,
        cancel: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/cancel/${reservationId}`,
        details: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/details/${reservationId}`,
        search: (companyId) => `/app/${companyId}/restaurant/reservation/search`,
        day: (companyId) => `/app/${companyId}/restaurant/reservation/day`,
        cancelMultiple: (companyId) => `/app/${companyId}/restaurant/reservation/cancel-multiple`,
        cancelPeriod: (companyId) => `/app/${companyId}/restaurant/reservation/cancel-period`,
      },
      services: {
        get: (companyId) => `/app/${companyId}/restaurant/service-config/get`,
        create: (companyId) => `/app/${companyId}/restaurant/service-config/create`,
        update: (companyId) => `/app/${companyId}/restaurant/service-config/update`,
      },
    },
    giftVoucherType: {
      all: (companyId) => `/app/${companyId}/catalog/all`,
      details: (companyId, typeId) => `/app/${companyId}/catalog/details/${typeId}`,
      create: (companyId) => `/app/${companyId}/catalog/create`,
      update: (companyId, typeId) => `/app/${companyId}/catalog/update/${typeId}`,
      delete: (companyId, typeId) => `/app/${companyId}/catalog/delete/${typeId}`,
      deleteMultiple: (companyId) => `/app/${companyId}/catalog/delete-multiple`,
    },
    catalogCategory: {
      all: (companyId) => `/app/${companyId}/catalog-categories/all`,
      details: (companyId, categoryId) => `/app/${companyId}/catalog-categories/details/${categoryId}`,
      create: (companyId) => `/app/${companyId}/catalog-categories/create`,
      update: (companyId, categoryId) => `/app/${companyId}/catalog-categories/update/${categoryId}`,
      delete: (companyId, categoryId) => `/app/${companyId}/catalog-categories/delete/${categoryId}`,
    },
    catalogOption: {
      all: (companyId) => `/app/${companyId}/catalog-options/all`,
      details: (companyId, optionId) => `/app/${companyId}/catalog-options/details/${optionId}`,
      create: (companyId) => `/app/${companyId}/catalog-options/create`,
      update: (companyId, optionId) => `/app/${companyId}/catalog-options/update/${optionId}`,
      delete: (companyId, optionId) => `/app/${companyId}/catalog-options/delete/${optionId}`,
    },
    giftVoucher: {
      all: (companyId) => `/app/${companyId}/gift-voucher/all`,
      details: (companyId, giftVoucherId) => `/app/${companyId}/gift-voucher/details/${giftVoucherId}`,
      createManual: (companyId) => `/app/${companyId}/gift-voucher/create-manual`,
      update: (companyId, giftVoucherId) => `/app/${companyId}/gift-voucher/update/${giftVoucherId}`,
      search: (companyId, lastSixChars) => `/app/${companyId}/gift-voucher/search/${lastSixChars}`,
      templatePreview: (companyId, templateName) => `/app/${companyId}/gift-voucher/template-preview/${templateName}`,
      stats: {
        overall: (companyId) => `/app/${companyId}/gift-voucher/stats/overall`,
        periodic: (companyId) => `/app/${companyId}/gift-voucher/stats/current-period`,
        history: (companyId) => `/app/${companyId}/gift-voucher/stats/historical`,
        used: (companyId) => `/app/${companyId}/gift-voucher/stats/used-vouchers`,
        percentage: (companyId) => `/app/${companyId}/gift-voucher/stats/percentage-changes`,
        type: (companyId) => `/app/${companyId}/gift-voucher/stats/by-type`,
        status: (companyId) => `/app/${companyId}/gift-voucher/stats/status-summary`,
      },
    },
    transaction: {
      all: (companyId) => `/app/${companyId}/transaction/all`,
    },
    invoice: {
      all: (companyId) => `/app/${companyId}/invoice/all`,
      lastTen: (companyId) => `/app/${companyId}/invoice/last-ten`,
    },
  },
  public: {
    company: {
      details: '/public/company/details',
    },
    catalog: {
      all: '/public/catalog/all',
      details: '/public/catalog/details/:companyId/:giftVoucherTypeId',
    },
    catalogCategories: {
      all: (companyId) => `/public/catalog-categories/all/${companyId}`,
      details: (companyId, categoryId) => `/public/catalog-categories/details/${companyId}/${categoryId}`,
    },
    catalogOptions: {
      compatible: (companyId, giftVoucherTypeId) => `/public/catalog-options/compatible/${companyId}/${giftVoucherTypeId}`,
      details: (companyId, optionId) => `/public/catalog-options/details/${companyId}/${optionId}`,
    },
    giftVoucher: {
      createPaymentIntent: '/public/purchase/create-payment-intent',
      createOrder: '/public/purchase/create-gift-voucher',
      updateOrder: '/public/purchase/update-gift-voucher',
      transactionDetails: '/public/purchase/transaction-details/:transactionId',
    },
    commission: {
      calculate: '/public/commission/calculate/:companyId/:baseAmount',
    },
    maintenance: {
      check: '/public/maintenance/check',
    },
  },
  admin: {
    accounts: '/admin/accounts',
    users: '/admin/users',
    companies: '/admin/companies',
    accountCreate: '/admin/account/create',
    adminCreate: '/admin/admin/create',
    giftVoucherTypes: {
      all: '/admin/gift-voucher-types/all',
      details: (id) => `/admin/gift-voucher-types/details/${id}`,
      create: '/admin/gift-voucher-types/create',
      update: (id) => `/admin/gift-voucher-types/update/${id}`,
      delete: (id) => `/admin/gift-voucher-types/delete/${id}`,
    },
    giftVoucher: {
      all: '/admin/gift-voucher/all',
      details: '/admin/gift-voucher/details',
    },
    companies: {
      all: '/admin/companies/all',
      details: (id) => `/admin/companies/details/${id}`,
      update: (id) => `/admin/companies/update/${id}`,
      stats: '/admin/companies/stats',
      monthlyCommissionsRevenue: (year, month) => `/admin/companies/monthly-commissions-revenue/${year}/${month}`,
      topByCommissions: '/admin/companies/top-by-commissions',
      delete: (id) => `/admin/companies/delete/${id}`,
    },
    invoices: {
      all: '/admin/invoices/all',
      details: (id) => `/admin/invoices/details/${id}`,
      company: (companyId) => `/admin/invoices/company/${companyId}`,
      stats: '/admin/invoices/stats',
      monthly: (year, month) => `/admin/invoices/monthly/${year}/${month}`,
      downloadZip: '/admin/invoices/download-zip',
    },
  },
  // Endpoints pour l'API externe avec authentication par API key
  externalApi: {
    reservation: {
      config: (companyId) => `/v1/reservation/config/${companyId}`,
      dates: (companyId) => `/v1/reservation/dates/${companyId}`,
      timeslots: (companyId) => `/v1/reservation/timeslots/${companyId}`,
      create: (companyId) => `/v1/reservation/create/${companyId}`,
    },
    giftVoucher: {
      types: (companyId) => `/v1/gift-vouchers/catalog/${companyId}`,
      categories: (companyId) => `/v1/gift-vouchers/categories/${companyId}`,
    },
    company: {
      details: (companyId) => `/v1/company/${companyId}`,
    },
  },
};

/**
 * Gestion du rafraîchissement des tokens
 */
let isRefreshing = false;
let failedQueue = [];

/**
 * Traite la file d'attente des requêtes échouées
 * @param {Error|null} error - Erreur éventuelle lors du rafraîchissement
 * @param {string|null} token - Nouveau token en cas de succès
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Effectue la requête de rafraîchissement du token
 * @return {Promise} Résultat de la requête de rafraîchissement
 */
const refreshTokenRequest = async () => {
  try {
    const {data} = await axios.post(`${HOST_API}/auth/refresh-token`, {}, {
      withCredentials: true,
    });
    return data;
  } catch (error) {
    console.error('Erreur lors du rafraîchissement du token:', error);
    return Promise.reject(error);
  }
};

/**
 * Gère la déconnexion en cas d'erreur critique
 */
const handleCriticalError = () => {
  // Nettoyage du localStorage
  localStorage.removeItem('authState');

  // On ajoute un flag pour indiquer une déconnexion récente
  localStorage.setItem('recentlyLoggedOut', 'true');

  // Redirection vers la page de login
  const loginPath = '/auth/login';
  if (window.location.pathname !== loginPath) {
    window.location.href = loginPath;
  }
};

/**
 * Liste des codes d'erreur nécessitant une déconnexion
 */
const CRITICAL_ERROR_CODES = [
  'TOKEN_INVALID',
  'TOKEN_EXPIRED',
  'USER_NOT_FOUND',
  'TOKEN_NOT_FOUND',
  'TOKEN_MISSING',
];

/**
 * Intercepteur pour les requêtes
 */
axiosInstance.interceptors.request.use(
    (config) => {
    // Ici on pourrait ajouter des headers supplémentaires si nécessaire
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
);

/**
 * Intercepteur pour les réponses
 */
axiosInstance.interceptors.response.use(
    // Succès
    (response) => response,

    // Erreur
    async (error) => {
      const originalRequest = error.config;

      // Si l'erreur vient du rafraîchissement du token, on déconnecte
      if (originalRequest.url?.includes('/auth/refresh-token')) {
        handleCriticalError();
        return Promise.reject(error);
      }

      // Gestion des erreurs 401 (non authentifié)
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
        // Si un rafraîchissement est déjà en cours, on met la requête en file d'attente
          try {
            await new Promise((resolve, reject) => {
              failedQueue.push({resolve, reject});
            });
            // Une fois le token rafraîchi, on réessaye la requête originale
            return axiosInstance(originalRequest);
          } catch (error) {
            return Promise.reject(error);
          }
        }

        // On marque la requête comme ayant déjà été réessayée
        originalRequest._retry = true;
        isRefreshing = true;

        try {
        // Tentative de rafraîchissement du token
          const data = await refreshTokenRequest();

          // Succès du rafraîchissement
          processQueue(null, data.accessToken);
          isRefreshing = false;

          // On réessaye la requête originale
          return axiosInstance(originalRequest);
        } catch (refreshError) {
        // Échec du rafraîchissement
          processQueue(refreshError, null);
          isRefreshing = false;
          handleCriticalError();
          return Promise.reject(refreshError);
        }
      }

      // Gestion des erreurs 403 (accès interdit)
      if (error.response?.status === 403) {
        const errorCode = error.response.data?.code;
        if (CRITICAL_ERROR_CODES.includes(errorCode)) {
          handleCriticalError();
        }
      }

      // Gestion des erreurs 429 (trop de requêtes)
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        if (retryAfter) {
        // On pourrait implémenter une logique de retry automatique ici
          console.warn(`Rate limit atteint. Réessayer dans ${retryAfter} secondes`);
        }
      }

      // Pour toute autre erreur, on la rejette simplement
      return Promise.reject(error);
    },
);

export default axiosInstance;
